import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { SystemSettingDTO, SystemSettingGroupDTO } from '../../../api/system-setting-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T, translate } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikTextField } from '../../../components/formik';
import { LoadingButton } from '../../../components/loading-button';
import { Loading } from '../../../components/basic/loading';

interface SaveSystemSettingModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedSystemSetting?: SystemSettingDTO;
    busy?: boolean;
    groups: SystemSettingGroupDTO[];
    onHide: () => void;
    onSave: (values: SystemSettingDTO) => void;
    onDelete?: (systemSetting: SystemSettingDTO) => void;
}

export function SaveSystemSettingModal({
    show,
    allowEdit,
    selectedSystemSetting,
    busy,
    groups,
    onHide,
    onSave,
    onDelete,
}: SaveSystemSettingModalProps) {
    const SystemSettingSchema = yup.object().shape({
        id: yup.string().required(' '),
        value: yup.string().required(' '),
        groupId: yup.string().required(' '),
    });

    const defaultValues: SystemSettingDTO = {
        id: selectedSystemSetting ? selectedSystemSetting.id : '',
        value: selectedSystemSetting ? selectedSystemSetting.value : '',
        groupId: selectedSystemSetting ? selectedSystemSetting.groupId : '',
    };

    if (!show) return <></>;

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={SystemSettingSchema}
            onSubmit={onSave}
        >
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>
                                {selectedSystemSetting
                                    ? 'edit-system-setting'
                                    : 'new-system-setting'}
                            </T>
                            <bd.Flex align="center" gap={2}>
                                <LoadingButton
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit || busy}
                                    icon={<SvgSave />}
                                    loading={busy}
                                />

                                {selectedSystemSetting && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedSystemSetting)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="id"
                                name="id"
                                readOnly={!allowEdit || !!selectedSystemSetting}
                                className="w-100"
                                handleEnter
                            />

                            <FormikTextField
                                label="groups"
                                name="groupId"
                                items={[
                                    { id: '', title: '' },
                                    ...groups.map(x => ({
                                        id: x.id,
                                        title: translate(x.title),
                                    })),
                                ]}
                                readOnly={!allowEdit}
                                className="w-100"
                                handleEnter
                            />

                            <FormikTextField
                                type="textarea"
                                label="value"
                                name="value"
                                readOnly={!allowEdit}
                                className="w-100"
                                handleEnter
                            />
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
